import { ProcessDataProducerEventArgs } from ".";
import { DEFAULT_EMPTY_CELL_VALUE } from "../../config";
import { YieldType } from "../../globalTypes";
import { getQuoteFeederIdByName } from "../../sourceServices";
import {
  ASK_IDX,
  BID_IDX,
  DATA_PRODUCER_TABLE_NAME,
  QUOTE_FEED_IDX,
  ROW_ID_COLUMN,
  UPDATE_BUTTON_COLUMN,
} from "../constants";
import { resetStylesForUpdatedSecurityProducer } from "../resetStylesForUpdatedSecurityProducer";
import {
  getEditedSecurityData,
  pendingToUploadButtonStyles,
  updateQuoteFeedCellFormat,
  validateAndSetYieldValue,
  validateYieldValues,
} from "./utils";

interface BroadcastEditedDataByAllocationArgs extends Omit<ProcessDataProducerEventArgs, "isOnChanged" | "range"> {
  editedColumn: number;
  editedRow: number;
}

export const broadcastEditedDataByAllocation = async ({
  context,
  worksheet,
  editedColumn: relativeColumn,
  editedRow,
  onSaveSecurityInStaging,
  updatedValue,
  updatedValueYieldType,
  onSetQuoteFeedAsNone,
}: BroadcastEditedDataByAllocationArgs) => {
  const relativeRow = editedRow + 1;
  const table = worksheet.tables.getItemOrNullObject(DATA_PRODUCER_TABLE_NAME);
  const completeRow = worksheet.getRange(ROW_ID_COLUMN + relativeRow + ":" + UPDATE_BUTTON_COLUMN + relativeRow);

  completeRow.load("values");
  await context.sync();

  const rowValues = completeRow.values[0];
  const quoteFeedID = await getQuoteFeederIdByName(context, rowValues[QUOTE_FEED_IDX]);
  const editedSecurityData = getEditedSecurityData({ rowValues, quoteFeedID, relativeRow });

  if (relativeColumn === QUOTE_FEED_IDX) {
    if (quoteFeedID === "none") {
      resetStylesForUpdatedSecurityProducer({
        sentRow: relativeRow,
        validateYieldValues: false,
      });

      onSetQuoteFeedAsNone({ securityProducerId: editedSecurityData.securityProducerId });

      return;
    }

    const askValue = rowValues[ASK_IDX];
    const bidValue = rowValues[BID_IDX];

    if (
      askValue !== null &&
      bidValue !== null &&
      (await validateYieldValues({
        askValue,
        bidValue,
        relativeRow,
        editedSecurityData,
        table,
      }))
    ) {
      return;
    }

    await updateQuoteFeedCellFormat({ table, relativeColumn, relativeRow, context });
    pendingToUploadButtonStyles(table, relativeRow - 1);

    const securityDataWithFallback = {
      ...editedSecurityData,
      isFallback: false
    };

    onSaveSecurityInStaging(securityDataWithFallback);
    return;
  }

  if (quoteFeedID === "none") return;

  let yieldType: YieldType;
  let yieldValue: string | number;
  let isFallback: boolean;

  if (updatedValueYieldType && updatedValue) {
    yieldType = updatedValueYieldType;
    yieldValue = updatedValue;
    isFallback = true;
  } else {
    yieldType = relativeColumn === BID_IDX ? YieldType.BID : YieldType.ASK;
    yieldValue = rowValues[relativeColumn];
    isFallback = false;
  }

  if (
    yieldValue === DEFAULT_EMPTY_CELL_VALUE ||
    !(await validateAndSetYieldValue({
      yieldType,
      yieldValue,
      relativeRow: relativeRow - 1,
      editedSecurityData,
      table,
      relativeColumn,
    }))
  ) {
    return;
  }

  pendingToUploadButtonStyles(table, relativeRow - 1);
  await context.sync();

  const securityDataWithFallback = {
    ...editedSecurityData,
    isFallback
  };

  onSaveSecurityInStaging(securityDataWithFallback);
  await context.sync();
};
